// NEW_FILE_JUSTIFIED: State management hook for DependantsSection — extracted per max 2 useState rule
import { useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';
import type {
  IDependant,
  IDependantFormData,
  IUseDependantsSectionArgs,
  TDependantRelationship,
} from '@/@types/dependants';

export function useDependantsSection({
  parentType,
  employeeId,
  leadId,
  orgId,
}: IUseDependantsSectionArgs) {
  const [editingId, setEditingId] = useState<Id<'dependants'> | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Both queries called unconditionally — rules of hooks require consistent call order
  // 'skip' sentinel tells Convex to not execute the query
  const byEmployee = useQuery(
    api.insurance.dependants.listByEmployee,
    parentType === 'employee' && employeeId ? { employeeId } : 'skip'
  );
  const byLead = useQuery(
    api.insurance.dependants.listByLead,
    parentType === 'customer' && leadId ? { leadId } : 'skip'
  );

  const createDependant = useMutation(api.insurance.dependants.create);
  const updateDependant = useMutation(api.insurance.dependants.update);
  const removeDependant = useMutation(api.insurance.dependants.remove);
  const addDocument = useMutation(api.insurance.dependants.addDocument);
  const removeDocument = useMutation(api.insurance.dependants.removeDocument);

  const dependants = parentType === 'employee' ? byEmployee : byLead;
  const isLoading = dependants === undefined;

  const handleCreate = async (data: IDependantFormData) => {
    await createDependant({
      orgId,
      parentType,
      employeeId,
      leadId,
      name: data.name,
      dateOfBirth: data.dateOfBirth || undefined,
      memberId: data.memberId || undefined,
      relationship: data.relationship,
    });
    setIsAdding(false);
  };

  const handleUpdate = async (dependantId: Id<'dependants'>, data: Partial<IDependantFormData>) => {
    await updateDependant({
      dependantId,
      name: data.name,
      dateOfBirth: data.dateOfBirth,
      memberId: data.memberId,
      relationship: data.relationship as TDependantRelationship | undefined, // AS_JUSTIFIED: Partial<IDependantFormData>.relationship is already TDependantRelationship, just possibly undefined
    });
    setEditingId(null);
  };

  const handleRemove = async (dependantId: Id<'dependants'>) => {
    await removeDependant({ dependantId });
  };

  const handleUploadDocument = async (
    dependantId: Id<'dependants'>,
    storageId: Id<'_storage'>,
    fileName: string,
    documentType: string
  ) => {
    await addDocument({ dependantId, storageId, fileName, documentType });
  };

  const handleRemoveDocument = async (dependantId: Id<'dependants'>, storageId: Id<'_storage'>) => {
    await removeDocument({ dependantId, storageId });
  };

  return {
    dependants: (dependants ?? []) as IDependant[], // AS_JUSTIFIED: ?? [] falls back to empty array when query is skipped; type is IDependant[] in both branches
    isLoading,
    editingId,
    setEditingId,
    isAdding,
    setIsAdding,
    handleCreate,
    handleUpdate,
    handleRemove,
    handleUploadDocument,
    handleRemoveDocument,
  };
}
